<script setup>
import axios from "axios";
import {computed, onMounted, ref, watch} from "vue";
import { useAppStateStore } from '@/stores/AppStateStore.js';

const appStateStore = useAppStateStore();

const aiUsageTool = {
    title: _mft('dashboard:aiSpend.label'),
    component: 'utility-ai-usage',
    toolName: 'ai-usage',
    icon: 'fa-light fa-chart-pie',
    openIn: 'teleport'
}

const availableTools = [
    {
        title: _mft('helper:utilityUrlShortener'),
        component: 'utility-url-shortener',
        toolName: 'url-shortener',
        options: {paginate: true},
        icon: 'fa-light fa-link',
        openIn: 'popup'
    },
    {
        title: _mft('helper:utilityPasswordGenerator'),
        component: 'utility-password-generator',
        toolName: 'password-generator',
        icon: 'fa-light fa-key',
        openIn: 'popup'
    },
    {
        title: _mft('helper:utilityBase64Coder'),
        component: 'utility-base64-coder',
        toolName: 'base64-coder',
        icon: 'fa-light fa-code',
        openIn: 'popup'
    },
    {
        title: _mft('helper:utilityQrGenerator'),
        component: 'utility-qr-generator',
        toolName: 'qr-generator',
        icon: 'fa-light fa-qrcode',
        openIn: 'popup'
    },
    {
        name: 'Time Tracking',
        component: 'time-tracking-form',
        toolName: 'time-tracking',
        icon: 'fa-light fa-clock',
        openIn: 'popup'
    },
    {
        name: 'Media Upload',
        component: 'media-upload',
        toolName: 'media-upload',
        icon: 'fa-light fa-upload',
        openIn: 'popup'
    },
    {
        name: 'Add contact',
        component: 'toolbar-add-lead',
        toolName: 'toolbar-add-lead',
        icon: 'fa-light fa-user-plus',
        openIn: 'sidebar'
    },
    {
        name: 'Tasks',
        component: 'utility-manage-tasks',
        toolName: 'utility-manage-tasks',
        icon: 'fa-light fa-list-check',
        openIn: 'sidebar'
    },
]
const intercomOpen = ref(false)
const showingComponent = ref(null)
const transitioned = ref(false)
const pinnedHelpers = ref([])
const delegatedGrants = ref([])
const delegatedSession = computed(() => window.myflow?.delegatedAccess ?? { switched: false })
const showDelegatedShortcut = computed(
    () => !delegatedSession.value.switched && delegatedGrants.value.length > 0
)
const openIntercom = () => {
    window.Intercom('show')
}

const closeIntercom = () => {
    window.Intercom('hide')
}

const toggleTool = (tool) => {
    if (showingComponent.value?.toolName === tool.toolName) {
        showingComponent.value = null
        return
    }
    showingComponent.value = tool
}

const loadDelegatedAccess = async () => {
    try {
        const response = await axios.get(route('company.user.delegated-access.received'))
        delegatedGrants.value = response.data.grants ?? []
    } catch (error) {
        delegatedGrants.value = []
    }
}

const openDelegatedSwitcher = () => {
    window.dispatchEvent(new CustomEvent('delegated-access:open-switcher'))
}

const modalOpen = computed(() => {
    return showingComponent.value !== null
})

const displayedTools = computed(() => {
    return availableTools.filter(tool => pinnedHelpers.value.includes(tool.toolName))
})

const minimizedClass = computed(() => {
    return appStateStore.bottomRightToolbarMinimized ? 'translate-x-full -mr-4' : ''
})

watch(modalOpen, async (value) => {
    if (value) {
        setTimeout(function(){
            transitioned.value = true
        },1)
    } else {
        transitioned.value = false
    }
})

watch(intercomOpen, (newVal) => {
    if (newVal) {
        openIntercom()
    } else {
        closeIntercom()
    }
})

onMounted(() => {
    _mfPrefGet('pinned-toolbar-items').then(data => {
        pinnedHelpers.value = data.split(',')
    })

    if (window.myflow?.user?.loggedIn) {
        loadDelegatedAccess()
    }
})

</script>
<template>
    <div
        class="fixed flex space-x-2 transform bottom-4 right-4 z-modal"
        :class="minimizedClass"
    >
        <div class="flex items-center">
            <button
                v-if="appStateStore.bottomRightToolbarMinimized"
                @click="appStateStore.toggleBottomRightToolbarMinimized()"
                class="border border-gray-200 hover:border-gray-300 shadow-lg transition-colors h-8 -translate-x-full w-4 pl-1 rounded-l-full bg-brandPrimary-500 hover:bg-brandPrimary-400 border-r-0"
            >
                <i
                    class="fa-light fa-chevron-right rotate-180 text-white"
                ></i>
            </button>
            <button
                v-else
                @click="appStateStore.toggleBottomRightToolbarMinimized()"
                class="border border-gray-200 hover:border-gray-300 shadow-lg transition-colors h-8 w-8 rounded-full bg-white"
            >
                <i
                    class="fa-light fa-chevron-right text-gray-800"
                ></i>
            </button>
        </div>

        <button-base
            v-for="(tool, t) in displayedTools"
            :key="t"
            :style-type="showingComponent?.toolName === tool.toolName ? 'primary' : 'secondary'"
            @click="toggleTool(tool)"
            size="sm"
            class="justify-center w-10"
        >
            <i
                class="text-xl"
                v-bind:class="tool.icon"
            ></i>
        </button-base>

        <button-base
            v-if="showDelegatedShortcut"
            style-type="secondary"
            size="sm"
            class="justify-center w-10"
            :title="_mft('navigation:backOffice.widget.switchCompany')"
            @click="openDelegatedSwitcher"
        >
            <i class="text-xl fa-light fa-person-to-door"></i>
        </button-base>

        <button-secondary
            @click="toggleTool(aiUsageTool)"
            size="sm"
            class="ml-2 !bg-blue-500 !text-white !border-blue-500"
            :title="aiUsageTool.title"
        >
            <i class="text-xl text-white fa-light fa-chart-pie"></i>
        </button-secondary>

        <button-secondary
            @click="intercomOpen = !intercomOpen"
            size="sm"
            class="!bg-myflowPurple !text-white !border-wlPrimary"
        >
            <i class="text-xl text-white fa-light fa-message"></i>
        </button-secondary>

        <div
            v-if="modalOpen && showingComponent.openIn === 'popup'"
            class="fixed inset-0 flex items-end justify-end pl-8 pr-5 z-modal-backdrop md:pr-5 pb-9"
        >
            <div
                class="fixed inset-0 transition-opacity z-modal-backdrop"
                :class="transitioned ? 'opacity-20' : 'opacity-0'"
                @click="showingComponent = null"
            ></div>
            <card-basic
                class="relative w-full max-w-sm transition-all origin-bottom-right z-modal min-w-72"
                :class="transitioned ? '' : 'opacity-0 scale-50'"
            >
                <div class="pt-2">
                    <h2 v-if="showingComponent.title">
                        {{ showingComponent.title }}
                    </h2>
                    <component
                        :is="showingComponent.component"
                        :options="showingComponent.options ? showingComponent.options : {}"
                        @close="showingComponent = null"
                    ></component>
                </div>
            </card-basic>
        </div>
        <slideout-with-slots
            v-if="modalOpen && showingComponent.openIn === 'sidebar'"
            @close="showingComponent = null"
        >
            <template #title>
                <h2>{{ showingComponent.name }}</h2>
            </template>

            <template #body>
                <component
                    :is="showingComponent.component"
                    :options="showingComponent.options ? showingComponent.options : {}"
                    @close="showingComponent = null"
                ></component>
            </template>
        </slideout-with-slots>
        <component
            v-if="modalOpen && showingComponent.openIn === 'teleport'"
            :is="showingComponent.component"
            :options="showingComponent.options ? showingComponent.options : {}"
            @close="showingComponent = null"
        ></component>
    </div>
</template>
